// src/admin/screens/actions/ReviewActionModal.tsx
// Calls public.admin_action_review(p_request_id, p_decision, p_review_notes).
// The RPC is expected from sub-project A and is shimmed in the local
// FUNCTION_EXTRAS until it lands. It atomically:
//   - verifies caller is super_admin and is NOT the original requester
//   - stamps reviewed_by/reviewed_at/review_notes and sets status to
//     approved|rejected
//   - on 'approve', executes the side-effect for the action's type
//     (restructure_loan, write_off_loan, refund_payment, etc.)
//
// Audit: always writes action.review with { decision, action_type } so the
// trail is independently queryable even if the RPC rolls the status row
// back on an error.
import { useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@admin/lib/supabase';
import { recordAudit } from '@admin/lib/audit';
import Modal from '@admin/components/Modal';
import Button from '@admin/components/Button';
import FormField from '@admin/components/FormField';
import { useToast } from '@admin/components/ToastProvider';
import { translateError } from '@admin/utils/translateError';
import type { ActionRequestRow } from '@admin/hooks/useActionRequests';
import styles from './ReviewActionModal.module.css';

interface Props {
  req: ActionRequestRow;
  onClose: (refresh: boolean) => void;
}

export default function ReviewActionModal({ req, onClose }: Props) {
  const toast = useToast();
  const qc = useQueryClient();
  const [decision, setDecision] = useState<'approve' | 'reject'>('approve');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (notes.trim().length < 10) {
      toast({ kind: 'error', msg: 'Review notes must be at least 10 characters.' });
      return;
    }
    setBusy(true);
    try {
      const { error } = await supabase.rpc('admin_action_review', {
        p_request_id: req.id,
        p_decision: decision,
        p_review_notes: notes.trim(),
      });
      if (error) throw error;
      await recordAudit({
        action: 'action.review',
        target_type: 'action_request',
        target_id: req.id,
        payload: {
          decision,
          action_type: req.action_type,
          notes: notes.trim(),
        },
      });
      toast({
        kind: 'success',
        msg: `Request ${decision === 'approve' ? 'approved' : 'rejected'}.`,
      });
      await qc.invalidateQueries({ queryKey: ['action-requests'] });
      if (req.target_loan_id) {
        await qc.invalidateQueries({ queryKey: ['loan', req.target_loan_id] });
      }
      if (req.target_borrower_id) {
        await qc.invalidateQueries({ queryKey: ['borrower', req.target_borrower_id] });
      }
      onClose(true);
    } catch (err) {
      toast({ kind: 'error', msg: translateError(err as Error) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => onClose(false)}
      title={`Review — ${req.action_type}`}
      width={560}
    >
      <form onSubmit={submit} className={styles.form}>
        <div className={styles.meta}>
          <p className={styles.metaLine}>
            <span className={styles.label}>Requested by:</span>{' '}
            {req.requested_by?.display_name ?? req.requested_by?.email ?? '—'}
          </p>
          <p className={styles.metaLine}>
            <span className={styles.label}>Request notes:</span>{' '}
            {req.request_notes ?? '—'}
          </p>
          <p className={styles.metaLine}>
            <span className={styles.label}>Payload:</span>
          </p>
          <pre className={styles.payload}>
            {JSON.stringify(req.request_payload, null, 2)}
          </pre>
        </div>

        <FormField label="Decision" htmlFor="ra-decision" required>
          <select
            id="ra-decision"
            value={decision}
            onChange={(e) =>
              setDecision(e.target.value === 'reject' ? 'reject' : 'approve')
            }
          >
            <option value="approve">Approve — execute the action</option>
            <option value="reject">Reject — do nothing</option>
          </select>
        </FormField>

        <FormField
          label="Review notes"
          htmlFor="ra-notes"
          hint="Required and audit-logged (min 10 characters)."
          required
        >
          <textarea
            id="ra-notes"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </FormField>

        <div className={styles.actions}>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onClose(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant={decision === 'approve' ? 'primary' : 'danger'}
            loading={busy}
          >
            {decision === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
